import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plug,
  Check,
  X,
  ChevronRight,
  RefreshCw,
  Trash2,
  Plus,
  AlertCircle,
  Share2,
  Briefcase,
  ShoppingCart,
  Users,
  MessageSquare,
  Search,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { useWorkspaceStore } from '../store/workspace';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface Provider {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  features: string[];
  authType: 'oauth2' | 'api_key' | 'webhook';
  isAvailable: boolean;
}

interface Integration {
  id: number;
  provider: string;
  provider_name: string;
  provider_username?: string;
  provider_email?: string;
  provider_icon?: string;
  provider_color?: string;
  provider_category?: string;
  status: 'connected' | 'disconnected' | 'error';
  last_used_at?: string;
  last_sync_at?: string;
  created_at: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
}

const categoryIcons: Record<string, React.FC<{ className?: string }>> = {
  social: Share2,
  productivity: Briefcase,
  ecommerce: ShoppingCart,
  crm: Users,
  communication: MessageSquare,
};

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

type ViewMode = 'integrations' | 'provider-detail';

export default function Integrations() {
  const { token } = useAuthStore();
  const { theme } = useThemeStore();
  const { currentWorkspace } = useWorkspaceStore();
  const isDark = theme === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();

  // Get view mode from URL, default to 'integrations'
  const viewModeParam = searchParams.get('view') as ViewMode | null;
  const viewMode: ViewMode = viewModeParam && ['integrations', 'provider-detail'].includes(viewModeParam)
    ? viewModeParam
    : 'integrations';

  // Helper function to update view mode in URL
  const setViewMode = useCallback((mode: ViewMode, providerId?: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (mode === 'integrations') {
      newParams.delete('view');
      newParams.delete('provider');
    } else {
      newParams.set('view', mode);
      if (providerId) {
        newParams.set('provider', providerId);
      }
    }
    setSearchParams(newParams, { replace: false });
  }, [searchParams, setSearchParams]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // API key form state (for API key integrations)
  const [apiKeyForm, setApiKeyForm] = useState({
    api_key: '',
    api_secret: '',
    shop_url: '',
  });

  const API_URL = import.meta.env.VITE_API_URL || 'https://sweetpilot.com';

  // Check for success/error in URL params (from OAuth callback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const provider = params.get('provider');
    const errorMsg = params.get('error');

    if (success === 'true' && provider) {
      setSuccessMessage('Integration successful');
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh data to show newly connected integration
      fetchData();
    } else if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [token, currentWorkspace?.id]);

  // Restore provider selection from URL when navigating back
  useEffect(() => {
    const providerId = searchParams.get('provider');
    if (viewMode === 'provider-detail' && providerId && providers.length > 0) {
      const provider = providers.find(p => p.id === providerId);
      if (provider && (!selectedProvider || selectedProvider.id !== provider.id)) {
        setSelectedProvider(provider);
      }
    }
  }, [viewMode, searchParams, providers, selectedProvider]);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const [providersRes, integrationsRes] = await Promise.all([
        fetch(`${API_URL}/integrations/providers`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/integrations${wsParam}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const providersData = await providersRes.json() as ApiResponse<{ providers: Provider[]; categories: Category[] }>;
      const integrationsData = await integrationsRes.json() as ApiResponse<Integration[]>;

      if (providersData.success && providersData.data) {
        setProviders(providersData.data.providers);
        setCategories(providersData.data.categories);
      }
      if (integrationsData.success && integrationsData.data) {
        setIntegrations(integrationsData.data);
      }
    } catch {
      setError('Failed to load integrations');
    }
    setLoading(false);
  };

  const connectProvider = async (provider: Provider) => {
    if (!token) return;
    setConnecting(true);
    setError(null);

    try {
      if (provider.authType === 'api_key') {
        // Show API key form
        setSelectedProvider(provider);
        setViewMode('provider-detail', provider.id);
        setConnecting(false);
        return;
      }

      const res = await fetch(`${API_URL}/integrations/connect/${provider.id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_id: currentWorkspace?.id || null,
        }),
      });

      interface ConnectResponse {
        success: boolean;
        data?: {
          requires_setup?: boolean;
          message?: string;
          auth_url?: string;
        };
        error?: string;
      }
      const data = await res.json() as ConnectResponse;

      if (data.success) {
        if (data.data?.requires_setup) {
          setError(data.data.message || 'Setup required');
        } else if (data.data?.auth_url) {
          // Redirect to OAuth
          window.location.href = data.data.auth_url;
        }
      } else {
        setError(data.error || 'Failed to connect');
      }
    } catch {
      setError('Failed to initiate connection');
    }
    setConnecting(false);
  };

  const connectWithApiKey = async () => {
    if (!token || !selectedProvider) return;
    setConnecting(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/integrations/connect/${selectedProvider.id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...apiKeyForm,
          workspace_id: currentWorkspace?.id || null,
        }),
      });

      const data = await res.json() as ApiResponse;

      if (data.success) {
        setSuccessMessage(`Connected to ${selectedProvider.name}!`);
        setViewMode('integrations', undefined);
        setApiKeyForm({ api_key: '', api_secret: '', shop_url: '' });
        fetchData();
      } else {
        setError(data.error || 'Failed to connect');
      }
    } catch {
      setError('Failed to connect');
    }
    setConnecting(false);
  };

  const disconnectIntegration = async (integrationId: number) => {
    if (!token) return;

    try {
      const wsHeaders: HeadersInit = currentWorkspace?.id
        ? { Authorization: `Bearer ${token}`, 'X-Workspace-Id': String(currentWorkspace.id) }
        : { Authorization: `Bearer ${token}` };

      const res = await fetch(`${API_URL}/integrations/${integrationId}`, {
        method: 'DELETE',
        headers: wsHeaders,
      });

      const data = await res.json() as ApiResponse;
      if (data.success) {
        setSuccessMessage('Integration disconnected');
        fetchData();
      } else {
        setError(data.error || 'Failed to disconnect');
      }
    } catch {
      setError('Failed to disconnect');
    }
  };

  const refreshIntegration = async (integrationId: number) => {
    if (!token) return;

    try {
      const wsHeaders: HeadersInit = currentWorkspace?.id
        ? { Authorization: `Bearer ${token}`, 'X-Workspace-Id': String(currentWorkspace.id) }
        : { Authorization: `Bearer ${token}` };

      const res = await fetch(`${API_URL}/integrations/${integrationId}/refresh`, {
        method: 'POST',
        headers: wsHeaders,
      });

      const data = await res.json() as ApiResponse;
      if (data.success) {
        setSuccessMessage('Token refreshed');
        fetchData();
      } else {
        setError(data.error || 'Failed to refresh');
      }
    } catch {
      setError('Failed to refresh');
    }
  };

  // Get connected provider IDs
  const connectedProviderIds = new Set(integrations.map(i => i.provider));

  // Filter and sort providers - available first, coming soon at bottom
  const filteredProviders = providers
    .filter(p => {
      const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
      const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           p.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      // Connected integrations first
      const aConnected = connectedProviderIds.has(a.id);
      const bConnected = connectedProviderIds.has(b.id);
      if (aConnected && !bConnected) return -1;
      if (!aConnected && bConnected) return 1;

      // Available (not coming soon) before unavailable
      if (a.isAvailable && !b.isAvailable) return -1;
      if (!a.isAvailable && b.isAvailable) return 1;

      // Alphabetically by name within same availability
      return a.name.localeCompare(b.name);
    });

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-accent-purple" />
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Messages */}
      <AnimatePresence>
        {(error || successMessage) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`p-4 rounded-xl flex items-center gap-3 ${
              error ? 'bg-red-500/10 border border-red-500/20' : 'bg-green-500/10 border border-green-500/20'
            }`}
          >
            {error ? (
              <AlertCircle className="w-5 h-5 text-red-400" />
            ) : (
              <Check className="w-5 h-5 text-green-400" />
            )}
            <span className={error ? 'text-red-400' : 'text-green-400'}>
              {error || successMessage}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.div variants={fadeInUp} className="flex items-center justify-between">
        <div>
          <h1 className={`text-xl sm:text-2xl font-bold mb-1 sm:mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Integrations</h1>
          <p className={`text-sm sm:text-base ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
            Connect your favorite tools and automate workflows
          </p>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {/* Integrations View */}
        {viewMode === 'integrations' && (
          <motion.div
            key="integrations"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {/* Connected Integrations */}
            {integrations.length > 0 && (
              <div>
                <h2 className={`text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                  Connected ({integrations.length})
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {integrations.map((integration) => (
                    <div
                      key={integration.id}
                      className={`rounded-xl sm:rounded-2xl p-4 sm:p-6 border flex flex-col ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}
                    >
                      <div className="flex items-start sm:items-center justify-between mb-2 sm:mb-3 gap-2">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                          <div
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${integration.provider_color}20` }}
                          >
                            <span
                              className="text-sm sm:text-lg font-bold"
                              style={{ color: integration.provider_color }}
                            >
                              {integration.provider_name?.[0] || integration.provider[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className={`font-medium text-sm sm:text-base truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {integration.provider_name || integration.provider}
                            </p>
                            {integration.provider_username && (
                              <p className={`text-xs sm:text-sm truncate ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                                {integration.provider_username}
                              </p>
                            )}
                          </div>
                        </div>
                        <span
                          className={`badge text-xs flex-shrink-0 ${
                            integration.status === 'connected'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}
                        >
                          {integration.status}
                        </span>
                      </div>

                      {integration.last_used_at && (
                        <p className={`text-xs mb-2 sm:mb-3 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          Last used: {new Date(integration.last_used_at).toLocaleDateString()}
                        </p>
                      )}

                      <div className="flex gap-2 mt-auto">
                        {integration.status === 'error' && (
                          <button
                            onClick={() => refreshIntegration(integration.id)}
                            className="btn-secondary flex-1 text-xs sm:text-sm py-1.5 sm:py-2"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Refresh
                          </button>
                        )}
                        <button
                          onClick={() => disconnectIntegration(integration.id)}
                          className="btn-secondary text-red-400 hover:text-red-300 text-xs sm:text-sm py-1.5 sm:py-2 px-2 sm:px-3"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search and Filter */}
            <div className={`rounded-xl sm:rounded-2xl p-3 sm:p-4 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex flex-col gap-3 sm:gap-4">
                {/* Search Input */}
                <div className="relative">
                  <Search className={`absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search integrations..."
                    className={`w-full h-10 sm:h-11 pl-10 sm:pl-12 pr-3 sm:pr-4 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:outline-none focus:border-accent-purple focus:ring-1 focus:ring-accent-purple/50 transition-all ${isDark ? 'bg-dark-700 border-dark-600 text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                  />
                </div>

                {/* Category Filters */}
                <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide pb-1 -mb-1">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium whitespace-nowrap transition-all ${
                      selectedCategory === 'all'
                        ? 'bg-accent-purple text-white shadow-glow-sm'
                        : isDark ? 'bg-dark-700 text-text-secondary hover:text-white hover:bg-dark-600' : 'bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                    }`}
                  >
                    All
                  </button>
                  {categories.map((cat) => {
                    const Icon = categoryIcons[cat.id] || Plug;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1.5 sm:gap-2 ${
                          selectedCategory === cat.id
                            ? 'bg-accent-purple text-white shadow-glow-sm'
                            : isDark ? 'bg-dark-700 text-text-secondary hover:text-white hover:bg-dark-600' : 'bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                        }`}
                      >
                        <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden xs:inline">{cat.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Available Providers - List View */}
            <div>
              <h2 className={`text-base sm:text-lg font-semibold mb-3 sm:mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Available Integrations ({filteredProviders.length})
              </h2>
              <div className={`rounded-xl sm:rounded-2xl border divide-y ${isDark ? 'bg-dark-800 border-border divide-dark-600' : 'bg-white border-slate-200 divide-slate-200 shadow-sm'}`}>
                {filteredProviders.map((provider) => {
                  const isConnected = connectedProviderIds.has(provider.id);
                  const Icon = categoryIcons[provider.category] || Plug;

                  return (
                    <div
                      key={provider.id}
                      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 gap-3 sm:gap-4 transition-colors ${!provider.isAvailable ? 'opacity-50' : ''} ${isDark ? 'hover:bg-dark-700/50' : 'hover:bg-slate-50'}`}
                    >
                      {/* Left: Icon and Info */}
                      <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                        <div
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${provider.color}15` }}
                        >
                          <span
                            className="text-lg sm:text-xl font-bold"
                            style={{ color: provider.color }}
                          >
                            {provider.name[0]}
                          </span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className={`font-semibold text-sm sm:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>{provider.name}</h3>
                            {isConnected && (
                              <span className="flex items-center gap-1 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                                <Check className="w-3 h-3" />
                                Connected
                              </span>
                            )}
                          </div>
                          <p className={`text-xs sm:text-sm line-clamp-2 sm:truncate ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
                            {provider.description}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1">
                            <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                              <Icon className="w-3 h-3" />
                              {categories.find(c => c.id === provider.category)?.name}
                            </span>
                            <span className={`hidden sm:inline ${isDark ? 'text-dark-500' : 'text-slate-300'}`}>•</span>
                            <div className="hidden sm:flex gap-1">
                              {provider.features.slice(0, 2).map((feature, i) => (
                                <span key={i} className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                                  {feature}{i < Math.min(provider.features.length - 1, 1) ? ',' : ''}
                                </span>
                              ))}
                              {provider.features.length > 2 && (
                                <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                                  +{provider.features.length - 2} more
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right: Connect Button - Hide Connected button on mobile (badge already shows next to title) */}
                      <div className="flex-shrink-0 sm:ml-4 self-end sm:self-auto">
                        {isConnected ? (
                          <button
                            disabled
                            className="hidden sm:flex items-center px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium bg-green-500/10 text-green-400 border border-green-500/20 cursor-default"
                          >
                            <Check className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                            Connected
                          </button>
                        ) : provider.isAvailable ? (
                          <button
                            onClick={() => connectProvider(provider)}
                            disabled={connecting}
                            className="px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium bg-gradient-to-r from-accent-purple to-accent-cyan text-white hover:shadow-glow-sm transition-all disabled:opacity-50"
                          >
                            <Plus className="w-3 h-3 sm:w-4 sm:h-4 inline-block mr-1 sm:mr-1.5" />
                            Connect
                          </button>
                        ) : (
                          <button
                            disabled
                            className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium cursor-not-allowed ${isDark ? 'bg-dark-600 text-text-muted' : 'bg-slate-100 text-slate-400'}`}
                          >
                            Coming Soon
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {filteredProviders.length === 0 && (
                  <div className="p-8 text-center">
                    <Search className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                    <p className={isDark ? 'text-text-secondary' : 'text-slate-500'}>No integrations found matching your search.</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Provider Detail / API Key Form */}
        {viewMode === 'provider-detail' && selectedProvider && (
          <motion.div
            key="provider-detail"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <button
              onClick={() => {
                setViewMode('integrations', undefined);
                setSelectedProvider(null);
              }}
              className={`flex items-center gap-2 ${isDark ? 'text-text-secondary hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back to Integrations
            </button>

            <div className={`rounded-2xl p-6 border max-w-lg ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex items-center gap-4 mb-6">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${selectedProvider.color}20` }}
                >
                  <span
                    className="text-2xl font-bold"
                    style={{ color: selectedProvider.color }}
                  >
                    {selectedProvider.name[0]}
                  </span>
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedProvider.name}</h2>
                  <p className={isDark ? 'text-text-secondary' : 'text-slate-500'}>{selectedProvider.description}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                    API Key *
                  </label>
                  <input
                    type="password"
                    value={apiKeyForm.api_key}
                    onChange={(e) => setApiKeyForm({ ...apiKeyForm, api_key: e.target.value })}
                    placeholder="Enter your API key"
                    className="input w-full"
                  />
                </div>

                {selectedProvider.id === 'stripe' && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      API Secret (optional)
                    </label>
                    <input
                      type="password"
                      value={apiKeyForm.api_secret}
                      onChange={(e) => setApiKeyForm({ ...apiKeyForm, api_secret: e.target.value })}
                      placeholder="Enter your API secret"
                      className="input w-full"
                    />
                  </div>
                )}

                {selectedProvider.id === 'woocommerce' && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Store URL *
                    </label>
                    <input
                      type="url"
                      value={apiKeyForm.shop_url}
                      onChange={(e) => setApiKeyForm({ ...apiKeyForm, shop_url: e.target.value })}
                      placeholder="https://yourstore.com"
                      className="input w-full"
                    />
                  </div>
                )}

                <button
                  onClick={connectWithApiKey}
                  disabled={connecting || !apiKeyForm.api_key}
                  className="btn-primary w-full"
                >
                  {connecting ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Plug className="w-4 h-4 mr-2" />
                  )}
                  Connect {selectedProvider.name}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
